/**
 * Canvas page · P2.6.b
 *
 * Mounts the WorkspaceBus subscription (live event stream + REST gap-fill)
 * via `useWorkspaceBus` and renders the chat panel for the current session.
 *
 * Layout v0.1: simple two-column · canvas placeholder left · chat panel right.
 * P2.6.c will land canvas rendering on the left side.
 *
 * codex-review 2026-04-29 P2.6.b R1 P1 fix:
 *   - backendUrl in dev uses empty string · `/api` and `/ws` are routed by
 *     Vite's proxy (vite.config.ts) to wrangler on :8787. Prior version
 *     hardcoded :8788 which doesn't match runbooks or proxy config and
 *     made the WS connection bypass the proxy.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { refreshSessionWorkflowState, useWorkspaceBus } from '../lib/use-workspace-bus';
import type { AuditRefreshResult } from '../lib/use-workspace-bus';
import { ChatPanel, type ChatAgentEntry } from '../components/ChatPanel';
import { CanvasView } from '../components/CanvasView';
import { NodeDetailPanel } from '../components/NodeDetailPanel';
import { CostBadge } from '../components/CostBadge';
import { Icon } from '../components/Icon';
import { avatarGlyph, fetchAgents, type AgentRow } from '../lib/fetch-agents';
import {
  fetchSessionParticipants,
  joinSessionParticipant,
  leaveSessionParticipant,
} from '../lib/fetch-session-participants';

export const Route = createFileRoute('/workspace/$workspaceId/canvas/$sessionId')({
  component: CanvasPage,
});

export interface SessionAuth {
  token: string | null;
  workspaceId: string | null;
}

type SyncStatus = 'idle' | 'syncing' | 'synced' | 'partial' | 'error';

export const CANVAS_BACKGROUND_SYNC_MS = 12_000;
export const CANVAS_SESSION_SYNC_MAX_PAGES = 100;

export type CanvasRecoveryAction =
  | { kind: 'login'; label: string }
  | { kind: 'workspace'; label: string; workspaceId: string };

function readAuthFromStorage(): SessionAuth {
  if (typeof window === 'undefined') return { token: null, workspaceId: null };
  return {
    token: sessionStorage.getItem('aiw.spa.token'),
    workspaceId: sessionStorage.getItem('aiw.spa.workspace_id'),
  };
}

export function canvasRecoveryActions(auth: SessionAuth, routeWorkspaceId: string): CanvasRecoveryAction[] {
  if (!auth.token) {
    return [{ kind: 'login', label: '去登录' }];
  }
  if (auth.workspaceId && auth.workspaceId !== routeWorkspaceId) {
    return [
      { kind: 'workspace', label: '回到已登录工作区', workspaceId: auth.workspaceId },
      { kind: 'login', label: '重新登录' },
    ];
  }
  return [];
}

/**
 * Backend base URL · same-origin in dev (Vite proxies /api and /ws to
 * wrangler :8787 · see vite.config.ts) and in prod (single-domain deploy).
 *
 * Override at build time via `VITE_BACKEND_URL` env if you need to point
 * the SPA at a remote backend (e.g. preview deploys).
 *
 * Pure function · takes deps as args so it's testable without mocking
 * `import.meta` (which Bun's test env can't intercept).
 */
export function backendUrl(deps?: {
  override?: string;
  origin?: string | null;
}): string {
  const override = deps?.override ?? (import.meta as any).env?.VITE_BACKEND_URL as string | undefined;
  if (override) return override.replace(/\/$/, '');
  const origin = deps?.origin ?? (typeof window !== 'undefined' ? window.location.origin : null);
  return origin ?? '';
}

export function toChatAgentEntries(rows: AgentRow[]): ChatAgentEntry[] {
  return rows
    .filter((row) =>
      (row.agent_kind === 'claude_code' || row.agent_kind === 'codex') &&
      row.agent_id !== 'planner' &&
      !row.agent_id.startsWith('review-'),
    )
    .map((row) => ({
      id: row.agent_id,
      display: `${avatarGlyph(row.agent_id)} · ${row.display_name}`,
      kind: row.agent_kind,
      hint: `${row.agent_kind}${row.harness_id ? ` · ${row.harness_id}` : ''}`,
      harness_id: row.harness_id,
    }))
    .sort((a, b) => a.id.localeCompare(b.id));
}

function CanvasPage() {
  const { workspaceId, sessionId } = Route.useParams();
  const navigate = useNavigate();
  const [auth, setAuth] = useState<SessionAuth>(() => readAuthFromStorage());
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [chatAgents, setChatAgents] = useState<ChatAgentEntry[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentsError, setAgentsError] = useState<string | null>(null);
  const [joinedAgentIds, setJoinedAgentIds] = useState<string[]>([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [participantsError, setParticipantsError] = useState<string | null>(null);
  const [participantUpdatingIds, setParticipantUpdatingIds] = useState<string[]>([]);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');
  const [syncDetail, setSyncDetail] = useState<string | null>(null);
  const syncInFlightRef = useRef(false);

  useEffect(() => {
    setAuth(readAuthFromStorage());
  }, []);

  // P3 P0-4 codex R1 P2 fix · reset selection when route params change.
  // Without this · TanStack reuse on session/workspace switch would carry
  // a stale node_id · either showing "未找到节点" or (worse) selecting a
  // same-id node from a different session.
  useEffect(() => {
    setSelectedNodeId(null);
    setJoinedAgentIds([]);
    setParticipantsError(null);
  }, [workspaceId, sessionId]);

  // P3 P0-4 · clicking the same node again toggles selection off (matches
  // common UX for accordion-like detail views).
  const handleSelectNode = (nodeId: string) => {
    setSelectedNodeId((cur) => (cur === nodeId ? null : nodeId));
  };

  useWorkspaceBus({
    backendUrl: backendUrl({}),
    workspaceId,
    token: auth.token ?? '',
    enabled: Boolean(auth.token) && auth.workspaceId === workspaceId,
    maxGapPages: CANVAS_SESSION_SYNC_MAX_PAGES,
  });

  useEffect(() => {
    if (!auth.token || auth.workspaceId !== workspaceId) return;
    let canceled = false;
    setAgentsLoading(true);
    setAgentsError(null);
    fetchAgents(backendUrl({}), auth.token)
      .then((rows) => {
        if (canceled) return;
        setChatAgents(toChatAgentEntries(rows));
      })
      .catch((e: any) => {
        if (canceled) return;
        setAgentsError(String(e?.message ?? e));
      })
      .finally(() => {
        if (!canceled) setAgentsLoading(false);
      });
    return () => { canceled = true; };
  }, [auth.token, auth.workspaceId, workspaceId]);

  const reloadParticipants = useCallback(async (isCanceled: () => boolean = () => false): Promise<void> => {
    if (!auth.token || auth.workspaceId !== workspaceId) {
      setJoinedAgentIds([]);
      return;
    }
    setParticipantsLoading(true);
    setParticipantsError(null);
    try {
      const result = await fetchSessionParticipants(backendUrl({}), auth.token, sessionId);
      if (isCanceled()) return;
      setJoinedAgentIds(result.participants.map((p) => p.agent_id));
    } catch (e: any) {
      if (isCanceled()) return;
      setParticipantsError(String(e?.message ?? e));
    } finally {
      if (!isCanceled()) setParticipantsLoading(false);
    }
  }, [auth.token, auth.workspaceId, workspaceId, sessionId]);

  useEffect(() => {
    let canceled = false;
    void reloadParticipants(() => canceled);
    return () => { canceled = true; };
  }, [reloadParticipants]);

  useEffect(() => {
    if (!auth.token || auth.workspaceId !== workspaceId) return;
    let canceled = false;
    void runSessionSync(true, () => canceled);
    const timer = setInterval(() => {
      void runSessionSync(false, () => canceled);
    }, CANVAS_BACKGROUND_SYNC_MS);
    return () => {
      canceled = true;
      clearInterval(timer);
    };
  }, [auth.token, auth.workspaceId, workspaceId, sessionId]);

  async function runSessionSync(visible: boolean, isCanceled: () => boolean = () => false): Promise<void> {
    if (!auth.token || auth.workspaceId !== workspaceId || syncInFlightRef.current) return;
    syncInFlightRef.current = true;
    if (visible) {
      setSyncStatus('syncing');
      setSyncDetail(null);
    }
    try {
      const result = await refreshSessionWorkflowState({
        backendUrl: backendUrl({}),
        token: auth.token,
        workspaceId,
        sessionId,
        maxPages: CANVAS_SESSION_SYNC_MAX_PAGES,
      });
      if (isCanceled()) return;
      setSyncStatus(result.completed ? 'synced' : 'partial');
      setSyncDetail(formatSyncDetail(result));
    } catch (e: any) {
      if (isCanceled()) return;
      if (visible) {
        setSyncStatus('error');
        setSyncDetail(String(e?.message ?? e));
      } else {
        console.warn('[canvas] background workflow sync failed', e);
      }
    } finally {
      syncInFlightRef.current = false;
    }
  }

  async function handleSyncAudit(): Promise<void> {
    if (syncStatus === 'syncing') return;
    await runSessionSync(true);
  }

  function markParticipantUpdating(agentId: string, updating: boolean): void {
    setParticipantUpdatingIds((cur) => {
      const next = new Set(cur);
      if (updating) next.add(agentId);
      else next.delete(agentId);
      return [...next];
    });
  }

  async function handleJoinAgent(agentId: string): Promise<void> {
    if (!auth.token || auth.workspaceId !== workspaceId) return;
    markParticipantUpdating(agentId, true);
    setParticipantsError(null);
    try {
      await joinSessionParticipant(backendUrl({}), auth.token, sessionId, agentId);
      setJoinedAgentIds((cur) => cur.includes(agentId) ? cur : [...cur, agentId]);
    } catch (e: any) {
      setParticipantsError(String(e?.message ?? e));
    } finally {
      markParticipantUpdating(agentId, false);
    }
  }

  async function handleLeaveAgent(agentId: string): Promise<void> {
    if (!auth.token || auth.workspaceId !== workspaceId) return;
    markParticipantUpdating(agentId, true);
    setParticipantsError(null);
    try {
      await leaveSessionParticipant(backendUrl({}), auth.token, sessionId, agentId);
      setJoinedAgentIds((cur) => cur.filter((id) => id !== agentId));
    } catch (e: any) {
      setParticipantsError(String(e?.message ?? e));
    } finally {
      markParticipantUpdating(agentId, false);
    }
  }

  function goLogin(): void {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('aiw.spa.token');
      sessionStorage.removeItem('aiw.spa.user');
      sessionStorage.removeItem('aiw.spa.workspace_id');
    }
    navigate({ to: '/login' });
  }

  if (!auth.token) {
    return (
      <div style={{ padding: 'var(--space-8)' }}>
        <h1>Canvas · {sessionId}</h1>
        <p style={{ color: 'var(--fg-soft)' }}>
          Not logged in · please go to <code>/login</code> first.
        </p>
        <RecoveryActions
          actions={canvasRecoveryActions(auth, workspaceId)}
          onLogin={goLogin}
          onWorkspace={(targetWorkspaceId) => navigate({ to: '/workspace/$workspaceId', params: { workspaceId: targetWorkspaceId } })}
        />
      </div>
    );
  }

  if (auth.workspaceId !== workspaceId) {
    return (
      <div style={{ padding: 'var(--space-8)' }}>
        <h1>Wrong workspace</h1>
        <p style={{ color: 'var(--fg-soft)' }}>
          Logged-in workspace <code>{auth.workspaceId}</code> · URL workspace <code>{workspaceId}</code>.
        </p>
        <RecoveryActions
          actions={canvasRecoveryActions(auth, workspaceId)}
          onLogin={goLogin}
          onWorkspace={(targetWorkspaceId) => navigate({ to: '/workspace/$workspaceId', params: { workspaceId: targetWorkspaceId } })}
        />
      </div>
    );
  }

  return (
    <div className="workbench-session-shell">
      <div
        className="workbench-subbar"
        data-testid="canvas-session-subbar"
      >
        <div className="workbench-subbar-main">
          <button
            type="button"
            onClick={() => navigate({ to: '/workspace/$workspaceId', params: { workspaceId } })}
            style={subnavButtonStyle}
            data-testid="canvas-back-to-workspace"
          >
            <span style={subnavButtonContentStyle}>
              <Icon name="chev-left" size={14} />
              工作台
            </span>
          </button>
          <div className="workbench-session-title">
            <span className="workbench-eyebrow">当前任务</span>
            <strong>任务板</strong>
            <code>{sessionId.slice(0, 8)}…</code>
          </div>
          <span
            className={`workbench-sync-chip ${syncStatus}`}
            data-testid="canvas-sync-status"
            title={syncDetail ?? undefined}
          >
            {syncStatusText(syncStatus, syncDetail)}
          </span>
        </div>
        <div className="workbench-subbar-actions">
          <button
            type="button"
            onClick={() => navigate({ to: '/workspace/$workspaceId/agents', params: { workspaceId } })}
            style={subnavButtonStyle}
            data-testid="canvas-open-agents"
          >
            <span style={subnavButtonContentStyle}>
              <Icon name="settings" size={14} />
              Agent
            </span>
          </button>
          <button
            type="button"
            onClick={() => { void handleSyncAudit(); }}
            disabled={syncStatus === 'syncing'}
            style={subnavButtonStyle}
            data-testid="canvas-sync-audit"
            title="从后端 audit log 拉取最新状态"
          >
            <span style={subnavButtonContentStyle}>
              <Icon name="refresh" size={14} />
              {syncStatus === 'syncing' ? '同步中' : '同步'}
            </span>
          </button>
          <CostBadge
            sessionId={sessionId}
            backendUrl={backendUrl({})}
            authToken={auth.token}
          />
        </div>
      </div>
      <div
        className="app-frame workbench-frame"
        style={{
          gridTemplateRows: '1fr',
        }}
      >
        <ChatPanel
          sessionId={sessionId}
          workspaceId={workspaceId}
          selectedNodeId={selectedNodeId}
          backendUrl={backendUrl({})}
          authToken={auth.token}
          agents={chatAgents}
          agentsLoading={agentsLoading}
          agentsError={agentsError}
          joinedAgentIds={joinedAgentIds}
          participantsLoading={participantsLoading}
          participantsError={participantsError}
          participantUpdatingIds={participantUpdatingIds}
          onJoinAgent={(agentId) => { void handleJoinAgent(agentId); }}
          onLeaveAgent={(agentId) => { void handleLeaveAgent(agentId); }}
        />
        <CanvasView
          sessionId={sessionId}
          workspaceId={workspaceId}
          backendUrl={backendUrl({})}
          authToken={auth.token}
          selectedNodeId={selectedNodeId}
          onSelectNode={handleSelectNode}
        />
        <NodeDetailPanel
          sessionId={sessionId}
          workspaceId={workspaceId}
          nodeId={selectedNodeId}
          onClose={() => setSelectedNodeId(null)}
          backendUrl={backendUrl({})}
          authToken={auth.token ?? undefined}
        />
      </div>
    </div>
  );
}

function RecoveryActions({
  actions,
  onLogin,
  onWorkspace,
}: {
  actions: CanvasRecoveryAction[];
  onLogin: () => void;
  onWorkspace: (workspaceId: string) => void;
}): JSX.Element | null {
  if (actions.length === 0) return null;
  return (
    <div style={recoveryActionsStyle}>
      {actions.map((action) => (
        <button
          key={`${action.kind}-${action.label}`}
          type="button"
          style={subnavButtonStyle}
          data-testid={action.kind === 'login' ? 'canvas-recovery-login' : 'canvas-recovery-workspace'}
          onClick={() => {
            if (action.kind === 'login') onLogin();
            else onWorkspace(action.workspaceId);
          }}
        >
          <span style={subnavButtonContentStyle}>
            <Icon name={action.kind === 'login' ? 'user' : 'chev-left'} size={14} />
            {action.label}
          </span>
        </button>
      ))}
    </div>
  );
}

export function formatSyncDetail(result: AuditRefreshResult): string {
  if (result.workspaceChanged) return '工作区已切换，已停止同步';
  if (!result.completed) {
    return result.eventsAdded > 0
      ? `已同步 ${result.eventsAdded} 条，仍有更多更新`
      : '仍有更多更新';
  }
  return result.eventsAdded > 0
    ? `已同步 ${result.eventsAdded} 条更新${result.snapshotApplied ? `，校准 ${result.snapshotNodeCount ?? 0} 个节点` : ''}`
    : result.snapshotApplied
      ? `已校准 ${result.snapshotNodeCount ?? 0} 个节点`
      : '已是最新';
}

export function syncStatusText(status: SyncStatus, detail: string | null): string {
  if (status === 'idle') return '实时同步';
  if (status === 'syncing') return '正在拉取最新状态';
  if (status === 'synced') return detail ?? '已同步';
  if (status === 'partial') return detail ?? '部分同步';
  return detail ? `同步失败：${detail}` : '同步失败';
}

const subnavButtonStyle: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 12,
  fontWeight: 700,
  padding: '4px 9px',
  border: '1.3px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  cursor: 'pointer',
};

const subnavButtonContentStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 5,
  whiteSpace: 'nowrap',
};

const recoveryActionsStyle: CSSProperties = {
  display: 'flex',
  gap: 8,
  flexWrap: 'wrap',
  marginTop: 16,
};
